import { GalleryAction } from '../gallery/galleryAction';
import {
  GRAPHIC_UPLOADER_GALLERY_MODE,
  GraphicUploaderGalleryMode,
} from './types';

export {
  DEFAULT_FILE_PREFIX,
  DOCUMENTATION_FILE_PREFIX,
  buildUploaderTargetPath,
  canAutoOpenFromCurrentPath,
  createDocumentPath,
  getRouteFileId,
  isPathInUploaderScope,
  isSamePath,
  removeTrailingSlash,
  resolveAlternateFilePrefix,
  resolveFilePrefix,
} from './routePath';

export const ADD_TILE_ANIMATION_DURATION_MS = 240;

export const editableGalleryActions = [
  GalleryAction.DRAW,
  GalleryAction.DOWNLOAD,
  GalleryAction.EDIT,
  GalleryAction.ROTATE,
  GalleryAction.REMOVE,
];

export const documentationNavigationActions = [
  GalleryAction.GO_TO_DOCUMENTATION_FILE,
  GalleryAction.SHARE_LINK,
];

export const defaultGalleryActions = [
  ...editableGalleryActions,
  ...documentationNavigationActions,
];

export const defaultGalleryActionsWithExport = [
  GalleryAction.REFRESH_PREVIEW,
  ...defaultGalleryActions,
  GalleryAction.EXPORT_TO_DOCUMENTATION,
];

export const issueLinkedDocumentationGalleryActions = [
  GalleryAction.UNLINK_LINKED_DOCUMENTATION,
  GalleryAction.DOWNLOAD,
  ...documentationNavigationActions,
];

export const GALLERY_ACTIONS_BY_MODE: Record<
  GraphicUploaderGalleryMode,
  GalleryAction[]
> = {
  [GRAPHIC_UPLOADER_GALLERY_MODE.DEFAULT]: defaultGalleryActions,
  [GRAPHIC_UPLOADER_GALLERY_MODE.ISSUE_ATTACHMENTS]:
    defaultGalleryActionsWithExport,
  [GRAPHIC_UPLOADER_GALLERY_MODE.ISSUE_LINKED_DOCUMENTATION]:
    issueLinkedDocumentationGalleryActions,
};

export function resolveGalleryActions(params: {
  galleryMode: GraphicUploaderGalleryMode;
}): GalleryAction[] {
  return GALLERY_ACTIONS_BY_MODE[params.galleryMode];
}

export function filesStringSelector(files: { localId?: number }[]): string {
  return files.map((file) => file.localId || 'unsaved_file_').join(',');
}

export function fileIdSelector(file: {
  localId?: number;
}): number | undefined {
  return file.localId;
}

/**
 * Stable identity for a file in the gallery's displayed (sorted) list.
 * `localId`/`_id` cover synced files; a not-yet-synced file (freshly
 * selected, no `_id`/`localId` yet) falls back to `sameFileKey` (title+size)
 * so the gallery can still track which entry the user has open across
 * re-sorts/re-syncs of the underlying list.
 */
export function resolveGalleryFileIdentity(file: {
  _id?: string;
  localId?: number;
  title?: string;
  data?: { name?: string };
  size?: number;
}): string {
  if (typeof file._id === 'string' && file._id.length > 0) {
    return `id:${file._id}`;
  }

  if (typeof file.localId === 'number' && Number.isFinite(file.localId)) {
    return `local:${file.localId}`;
  }

  const name = file.title ?? file.data?.name ?? '';
  return `key:${name}|${typeof file.size === 'number' ? file.size : ''}`;
}

/**
 * Re-resolves the gallery slide index after `files` changes shape/order
 * (new upload prepended, main-image resort, server sync) so the gallery
 * keeps following the same file the user has open instead of silently
 * drifting to whatever now sits at the old numeric index (which can also
 * land past the end of the array, e.g. after a re-sort of a shorter list).
 */
export function resolveGallerySlideAfterFilesChange<
  T extends {
    _id?: string;
    localId?: number;
    title?: string;
    data?: { name?: string };
    size?: number;
  }
>(previousFiles: T[], nextFiles: T[], currentSlide: number): number {
  if (nextFiles.length === 0) {
    return 0;
  }

  const currentFile = previousFiles[currentSlide];
  if (!currentFile) {
    return Math.min(currentSlide, nextFiles.length - 1);
  }

  const identity = resolveGalleryFileIdentity(currentFile);
  const nextIndex = nextFiles.findIndex(
    (file) => resolveGalleryFileIdentity(file) === identity
  );

  if (nextIndex !== -1) {
    return nextIndex;
  }

  return Math.min(currentSlide, nextFiles.length - 1);
}
